import { FC } from "react";
import styled from "styled-components";
import PieRing from "src/components/charts/PieRing";
import { chartColors, colorMapFor } from "src/components/Donut";
import { colors } from "src/theme/colors";
import { text } from "src/theme/ui";

interface IPieChart {
  data: { key: string; value: number }[];
  legend?: boolean;
  /** The ring's diameter. One figure, because it is a picture of a whole. */
  size?: number;
  /** Print the share allocated so far in the middle of the ring. */
  showTotal?: boolean;
}

/**
 * The part of the whole that has NOT been given to anything.
 *
 * A ring is a picture of a whole, and nivo normalises whatever it is handed
 * — so three weights of 10, 50 and 20 came out as a complete circle, and a
 * builder that was eighty per cent allocated looked finished. The shortfall
 * is a real part of this composition: it is the twenty per cent the reader
 * still has to place, and the dialog refuses to save until they have.
 *
 * Drawn as a hatch rather than a colour, which is what this product's rings
 * already do for an arc that is not a holding — it reads as absence on a
 * second channel, so it survives greyscale and a colour deficiency.
 */
const UNALLOCATED = "Unallocated";

/**
 * The ring's colours, for anything drawn BESIDE the ring.
 *
 * This was a hand-picked list of thirty hues — a cyan, a slate, a pink, then
 * five yellows in a row, a near-white at 18 and a green at 28 — used for the
 * dots next to the securities in the builder and the edit dialogs. The ring
 * itself is drawn from the validated categorical order in `Donut`, so the dot
 * beside "AAPL" and the arc for "AAPL" were different colours: the legend
 * disagreed with the chart it was a legend for.
 *
 * One source now, cycled the way the ring cycles it, so an index means the
 * same colour in both places.
 */
export const PieColors = Array.from(
  { length: 40 },
  (_, index) => chartColors[index % chartColors.length],
);

/**
 * The composition ring inside the builder and the edit dialogs.
 *
 * Drawn by the product's own `PieRing` (nivo) rather than by G2Plot: same
 * band, same gap, same rounded ends and the same hover — everything the
 * dashboard's ring does, because it IS the dashboard's ring with a different
 * hole size. What went with the library: a `statistic` block set to null to
 * suppress a figure nobody asked for, two named "interactions", and a
 * `label: { content: null }` — three ways of saying "draw the arcs and
 * nothing else".
 *
 * `legend` is kept in the signature because every call site passes it, but
 * these rings never had one: the list of securities beside them, with its own
 * coloured dots from `PieColors`, is the legend.
 */
/**
 * The ring's own box, and the reason this component exists as more than a
 * pass-through.
 *
 * `PieRing` draws into a frame that is `position: absolute; inset: 0` — it
 * is meant to be given a box, and the dashboard's ring gives it one. These
 * four dialogs did not: each wrapped it in a plain flex column with a height
 * and nothing else, so the frame skipped past them to the nearest positioned
 * ancestor, which is the MODAL, and drew a ring the size of the whole
 * dialog straight over the fields beside it. Reported on the asset-class
 * editor, and the same fault was in the smartfolio builder and both edit
 * dialogs.
 *
 * So the box belongs to the ring, not to whoever happens to be hosting it:
 * positioned, square, and centred in whatever column it is dropped into.
 * The one contract a caller cannot forget is the one it does not have to
 * remember.
 */
const Host = styled.div<{ $size: number }>`
  position: relative;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  max-width: 100%;
  margin: 0 auto;
`;

/* The figure in the middle: what the weights add up to so far. Set as a
   reading, not as an alarm — the arcs show the gap, the total row beside
   the ring carries the tone, and three red things saying one thing is
   noise. */
const Center = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  pointer-events: none;
`;

const Figure = styled.span`
  ${text.numeric}
  font-size: 19px;
  font-weight: 700;
  line-height: 1.2;
  color: ${colors.gray[900]};
`;

const Caption = styled.span`
  ${text.label}
  color: ${colors.gray[500]};
`;

export const PieChart: FC<IPieChart> = ({ data, size = 176, showTotal }) => {
  const rows = (data ?? []).filter((row) => Number(row?.value) > 0);
  const allocated = rows.reduce((sum, row) => sum + Number(row.value), 0);
  /* Half a point of slack: these weights arrive as thousandths and a
     hundred per cent can land at 99.97. */
  const short = allocated < 99.5 ? Math.round(100 - allocated) : 0;
  const slices = short ? [...rows, { key: UNALLOCATED, value: short }] : rows;
  const paint = colorMapFor(rows.map((row) => ({ x: row.key, y: row.value })));
  const colorOf = (id: string) =>
    id === UNALLOCATED ? colors.gray[300] : paint(id);

  return (
    <Host $size={size}>
      <PieRing
        data={slices.map((row) => ({
          id: row.key,
          value: Number(row.value),
        }))}
        colorOf={colorOf}
        /* The shortfall is absence, so it is drawn as one. */
        hatched={(id) => id === UNALLOCATED}
        /* A weight, so the tooltip trails a per-cent rather than leading a
           currency: these rings are drawn from basket weights that already
           sum to a hundred. */
        format={(value) => `${value}%`}
        /* The band the rest of the product draws. At 0.6 it was half the
           radius — a pie with a hole punched in it rather than a ring, and
           the only chart in the app wearing that weight. 0.68 is the figure
           `Donut` uses for a ring with nothing written in the middle. */
        inner={showTotal ? 0.8 : 0.68}
      />
      {showTotal && (
        <Center>
          <Figure>{Math.round(allocated)}%</Figure>
          <Caption>allocated</Caption>
        </Center>
      )}
    </Host>
  );
};
